import React from "react";
import { Link } from "react-router-dom";
import OTPScreen2 from "./OtpScreen2";

// Template10 — dark split screen: form on the left, internet image on the right,
// neon-teal accent. Generic (no company branding), self-contained styling.
const HERO_IMAGE =
  "https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?auto=format&fit=crop&w=1600&q=80";

const styles = `
.tmpl10-root{min-height:100vh;display:flex;font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;background:#0b1120;color:#e2e8f0;}
.tmpl10-root *{box-sizing:border-box;}
.tmpl10-panel{flex:1;display:flex;align-items:center;justify-content:center;padding:44px 24px;background:#0b1120;}
.tmpl10-card{width:100%;max-width:400px;}
.tmpl10-brand{display:flex;align-items:center;gap:12px;margin-bottom:36px;font-weight:600;font-size:17px;color:#f1f5f9;}
.tmpl10-logo{width:42px;height:42px;border-radius:12px;display:flex;align-items:center;justify-content:center;background:linear-gradient(135deg,#2dd4bf,#22d3ee);box-shadow:0 8px 22px rgba(45,212,191,.35);}
.tmpl10-title{font-size:27px;font-weight:700;margin:0 0 8px;letter-spacing:-.4px;color:#f8fafc;}
.tmpl10-sub{font-size:14px;color:#94a3b8;margin:0 0 32px;}
.tmpl10-field{margin-bottom:18px;}
.tmpl10-label{display:block;font-size:12.5px;font-weight:600;color:#cbd5e1;margin-bottom:7px;}
.tmpl10-input-wrap{position:relative;}
.tmpl10-input{width:100%;height:48px;border:1px solid #1e293b;border-radius:12px;padding:0 44px 0 16px;font-size:15px;color:#f1f5f9;background:#111a2e;transition:border-color .15s,box-shadow .15s;outline:none;}
.tmpl10-input::placeholder{color:#64748b;}
.tmpl10-input:focus{border-color:#2dd4bf;box-shadow:0 0 0 4px rgba(45,212,191,.15);}
.tmpl10-eye{position:absolute;right:10px;top:50%;transform:translateY(-50%);background:none;border:none;cursor:pointer;color:#64748b;padding:6px;display:flex;align-items:center;}
.tmpl10-eye:hover{color:#2dd4bf;}
.tmpl10-options{display:flex;align-items:center;justify-content:space-between;margin-bottom:24px;min-height:20px;}
.tmpl10-check{display:flex;align-items:center;gap:8px;font-size:13.5px;color:#94a3b8;cursor:pointer;user-select:none;}
.tmpl10-check input{width:16px;height:16px;accent-color:#2dd4bf;cursor:pointer;}
.tmpl10-link{font-size:13.5px;color:#2dd4bf;font-weight:600;text-decoration:none;}
.tmpl10-link:hover{text-decoration:underline;}
.tmpl10-btn{width:100%;height:50px;border:none;border-radius:12px;background:linear-gradient(135deg,#2dd4bf,#22d3ee);color:#04211f;font-size:16px;font-weight:700;cursor:pointer;transition:transform .12s,box-shadow .2s,opacity .2s;box-shadow:0 12px 26px rgba(45,212,191,.3);display:flex;align-items:center;justify-content:center;gap:8px;}
.tmpl10-btn:hover{transform:translateY(-1px);box-shadow:0 16px 34px rgba(45,212,191,.45);}
.tmpl10-btn:active{transform:translateY(0);}
.tmpl10-btn:disabled{opacity:.5;cursor:not-allowed;box-shadow:none;transform:none;}
.tmpl10-recaptcha{margin-bottom:18px;display:flex;justify-content:center;}
.tmpl10-otp-wrap{margin-top:8px;}
.tmpl10-hero{position:relative;flex:1.05;overflow:hidden;display:flex;flex-direction:column;justify-content:flex-end;padding:48px;min-height:100vh;}
.tmpl10-hero-img{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;opacity:.55;}
.tmpl10-hero-overlay{position:absolute;inset:0;background:linear-gradient(180deg,rgba(11,17,32,.25) 0%,rgba(11,17,32,.55) 55%,rgba(11,17,32,.92) 100%);}
.tmpl10-hero-content{position:relative;z-index:2;max-width:460px;}
.tmpl10-hero-content h1{font-size:38px;line-height:1.15;font-weight:700;margin:0 0 14px;letter-spacing:-.5px;color:#f8fafc;}
.tmpl10-hero-content p{font-size:15px;line-height:1.65;color:rgba(226,232,240,.8);margin:0;}
.tmpl10-hero-tag{position:relative;z-index:2;display:inline-flex;align-self:flex-start;align-items:center;gap:8px;margin-bottom:18px;padding:6px 14px;border-radius:999px;background:rgba(45,212,191,.14);border:1px solid rgba(45,212,191,.4);color:#5eead4;font-size:12.5px;font-weight:600;}
@media (max-width: 900px){
  .tmpl10-hero{display:none;}
}
`;

const LogoMark = () => (
  <span className="tmpl10-logo">
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <path d="M12 2l2.6 6.8L21 11l-6.4 2.2L12 20l-2.6-6.8L3 11l6.4-2.2z" fill="#04211f" />
    </svg>
  </span>
);

const EyeIcon = ({ off }) =>
  off ? (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  ) : (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );

class Template10 extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      email: "",
      password: "",
      isChecked: false,
      isShowPassword: false,
      twoFAStatus: null,
    };
  }

  updateTwoFAStatus = (t) => {
    this.setState({ twoFAStatus: t });
  };

  onLogin = () => {
    const { email, password, isChecked } = this.state;
    const { authenticationCallback, itemProperties, sessionId, twoFA } = this.props;
    if (isChecked && email !== "") {
      localStorage.username = email;
      localStorage.password = password;
      localStorage.checkbox = isChecked;
    }
    const payload = { email, password };

    if (twoFA) {
      this.props.setError(false);
      this.props.login(payload, () => {
        this.updateTwoFAStatus("OTP");
      });
    } else {
      if (itemProperties && itemProperties.authenticateUsingPlugins) {
        payload["authenticateUsingPlugins"] = true;
        payload["sessionId"] = sessionId;
      }
      this.props.setError(false);
      this.props.login(payload, authenticationCallback);
    }
  };

  togglePasswordVisiblity = () => {
    this.setState({ isShowPassword: !this.state.isShowPassword });
  };

  onKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      event.stopPropagation();
      this.onLogin();
    }
  };

  render() {
    const {
      label,
      hasForgotPassword,
      authTemplateTopFormText,
      itemProperties,
      authenticationCallback,
    } = this.props;

    const rememberMe =
      itemProperties && itemProperties.authConfig && itemProperties.authConfig.rememberMe;
    const otpScreenLabel = itemProperties && itemProperties.otpScreenLabel;
    const isOtp = this.state.twoFAStatus === "OTP";

    return (
      <div className="tmpl10-root">
        <style dangerouslySetInnerHTML={{ __html: styles }} />

        {/* Left form panel */}
        <div className="tmpl10-panel">
          <div className="tmpl10-card">
            <div className="tmpl10-brand">
              <LogoMark />
              <span>Your Platform</span>
            </div>

            {isOtp ? (
              <>
                <h2 className="tmpl10-title">
                  {otpScreenLabel ? otpScreenLabel : "One-Time Password"}
                </h2>
                <p className="tmpl10-sub">
                  Enter the verification code we just sent you.
                </p>
                <div className="tmpl10-otp-wrap">
                  <OTPScreen2
                    authenticationCallback={authenticationCallback}
                    requiredRole={itemProperties && itemProperties.requiredRole}
                    phoneNumber={this.state.email}
                  />
                </div>
              </>
            ) : (
              <>
                <h2 className="tmpl10-title">
                  {authTemplateTopFormText || "Sign in"}
                </h2>
                <p className="tmpl10-sub">Enter your credentials to access your account.</p>

                <div className="tmpl10-field">
                  <label className="tmpl10-label" htmlFor="tmpl10-email">Email</label>
                  <div className="tmpl10-input-wrap">
                    <input
                      id="tmpl10-email"
                      className="tmpl10-input"
                      value={this.state.email}
                      onChange={(e) => this.setState({ email: e.target.value })}
                      onKeyDown={this.onKeyDown}
                      placeholder="you@example.com"
                      type="text"
                      autoComplete="username"
                    />
                  </div>
                </div>

                <div className="tmpl10-field">
                  <label className="tmpl10-label" htmlFor="tmpl10-password">Password</label>
                  <div className="tmpl10-input-wrap">
                    <input
                      id="tmpl10-password"
                      className="tmpl10-input"
                      value={this.state.password}
                      onChange={(e) => this.setState({ password: e.target.value })}
                      onKeyDown={this.onKeyDown}
                      placeholder="Enter your password"
                      type={this.state.isShowPassword ? "text" : "password"}
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      className="tmpl10-eye"
                      onClick={this.togglePasswordVisiblity}
                      aria-label={this.state.isShowPassword ? "Hide password" : "Show password"}
                    >
                      <EyeIcon off={this.state.isShowPassword} />
                    </button>
                  </div>
                </div>

                <div className="tmpl10-options">
                  {rememberMe ? (
                    <label className="tmpl10-check">
                      <input
                        type="checkbox"
                        checked={this.state.isChecked}
                        onChange={(e) => this.setState({ isChecked: e.target.checked })}
                      />
                      Remember me
                    </label>
                  ) : (
                    <span />
                  )}
                  {hasForgotPassword ? (
                    <Link className="tmpl10-link" to="/app/forgotpassword">
                      Forgot password?
                    </Link>
                  ) : (
                    <span />
                  )}
                </div>

                {this.props.reCaptchaComponent ? (
                  <div className="tmpl10-recaptcha">{this.props.reCaptchaComponent}</div>
                ) : null}

                <button
                  type="button"
                  className="tmpl10-btn"
                  disabled={this.props.displayReCaptcha}
                  onClick={this.onLogin}
                >
                  {label && label.show ? label.text : "Sign in"}
                </button>
              </>
            )}
          </div>
        </div>

        {/* Right hero image (generic, hardcoded internet image) */}
        <div className="tmpl10-hero">
          <img className="tmpl10-hero-img" src={HERO_IMAGE} alt="" />
          <div className="tmpl10-hero-overlay" />
          <span className="tmpl10-hero-tag">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
            Secure access
          </span>
          <div className="tmpl10-hero-content">
            <h1>Build faster. Ship smarter.</h1>
            <p>
              One place for your workflows, data and teams — sign in and pick up
              right where you left off.
            </p>
          </div>
        </div>
      </div>
    );
  }
}

export default Template10;
